/**
 * Secondary motion drawn over the native frames — sprite sheets and puppets alike — so a story
 * does not look like cut-outs sliding over a picture:
 *
 * - a soft contact shadow under everyone standing on the floor;
 * - a walk bobs with its steps and leans into its travel, squashes as it sets off and settles
 *   with a little overshoot as it stops;
 * - every spoken word gives the speaker a small bounce (the recording's own word timings), with a
 *   slight alternating head-tilt, so talking reads as talking even on an idle-only sheet;
 * - the feeling a line is said with shows in the body: a happy hop, a surprised startle, a sad
 *   droop, an angry huff, a nervous tremble, a curious tilt;
 * - expressive gestures (happy, cheer, dance, surprised, angry…) pop as they start;
 * - standing still, everyone breathes.
 *
 * Pure in `(actor, t)`: story time only, no wall clock, no accumulation — a seek, a replay and the
 * video export draw the same frame. It never moves an actor's floor point: `lift` raises the body
 * above it (the shadow stays on the floor, z-order keeps the feet row) and the rest is scale and
 * lean about the feet. Under reduced motion only the shadow remains.
 */
import type { ActorState, StageState } from './runner'

export interface Liveliness {
  /** Scale multipliers about the feet. */
  sx: number
  sy: number
  /** Body raised above its floor point, as a fraction of its on-stage height. */
  lift: number
  /** Radians about the feet; + leans right (clockwise, y down). */
  lean: number
  /** Contact shadow, 0 (none) … 1 (standing on the floor). */
  shadow: number
}

export interface LivelinessOptions {
  /** On-stage body height, stage-height % — sets the stride of the walk bob. */
  heightPct: number
  /** Per-actor phase (seconds) so two idles never breathe in lock-step. */
  phase: number
  reducedMotion?: boolean
  /** Whoever else is speaking now: a listener leans in toward them and nods now and then. */
  listenTo?: Pick<ActorState, 'pos' | 'voice' | 'speaking'> | null
}

const TAU = Math.PI * 2
/** Stage aspect: 1 % of width is 1.7778× as many pixels as 1 % of height. */
const ASPECT = 1920 / 1080

const clamp01 = (v: number): number => (v <= 0 ? 0 : v >= 1 ? 1 : v)
const smooth = (u: number): number => {
  const x = clamp01(u)
  return x * x * (3 - 2 * x)
}
/** 0 → 1 → 0 over `x ∈ (0, 1)`, 0 outside. */
const bell = (x: number): number => (x > 0 && x < 1 ? Math.sin(Math.PI * x) : 0)

/** Seconds a walk takes to set off / settle (matches the runner's `LOCO_RAMP_S`). */
const RAMP_S = 0.25
const SET_OFF_S = 0.18
const SETTLE_S = 0.38

/** Feelings, by the words writers and the bake use for `say.emotion`. */
const FEELINGS: Readonly<Record<string, 'happy' | 'surprised' | 'sad' | 'angry' | 'scared' | 'curious'>> = {
  happy: 'happy', excited: 'happy', joyful: 'happy', glad: 'happy', proud: 'happy', delighted: 'happy',
  cheerful: 'happy', playful: 'happy', giggly: 'happy', silly: 'happy', thrilled: 'happy', eager: 'happy',
  surprised: 'surprised', amazed: 'surprised', shocked: 'surprised', startled: 'surprised', astonished: 'surprised', wow: 'surprised',
  sad: 'sad', sorry: 'sad', tired: 'sad', sleepy: 'sad', disappointed: 'sad', lonely: 'sad', upset: 'sad', gloomy: 'sad',
  angry: 'angry', cross: 'angry', grumpy: 'angry', frustrated: 'angry', annoyed: 'angry', mad: 'angry', huffy: 'angry',
  scared: 'scared', afraid: 'scared', nervous: 'scared', worried: 'scared', shy: 'scared', anxious: 'scared',
  curious: 'curious', thinking: 'curious', wondering: 'curious', puzzled: 'curious', confused: 'curious', thoughtful: 'curious',
}

export function feelingOf(emotion: string | null | undefined): (typeof FEELINGS)[string] | null {
  if (!emotion) return null
  return FEELINGS[emotion.trim().toLowerCase()] ?? null
}

/** Only unmistakable openers: a line with no stated feeling otherwise stays neutral. */
const LINE_FEELINGS: readonly (readonly [RegExp, string])[] = [
  [/^\s*(yay|hooray|hurray|woo-?hoo|yippee|we did it|i did it|you did it)\b/i, 'happy'],
  [/^\s*(wow|whoa|woah|ooh+|oh wow|oh look|look!)\b/i, 'surprised'],
  [/^\s*(oh no|aww+|oh dear)\b/i, 'sad'],
  [/^\s*(hey!|no fair|that'?s mine|stop it)/i, 'angry'],
  [/^\s*(hmm+|i wonder|what if)\b/i, 'curious'],
]

/** The line's stated feeling, else one read off an unmistakable opener ("Yay!", "Oh no…", "Hmm…"). */
export function lineFeeling(emotion: string | null | undefined, text: string): string | null {
  if (emotion && feelingOf(emotion)) return emotion
  for (const [re, feeling] of LINE_FEELINGS) if (re.test(text)) return feeling
  return emotion ?? null
}

/** Gestures that pop (stretch) as they start, and those that stomp (squash). */
const POP_CLIPS: ReadonlySet<string> = new Set(['happy', 'cheer', 'celebrate', 'dance', 'surprised', 'greet', 'wave', 'jump_joyful'])
const STOMP_CLIPS: ReadonlySet<string> = new Set(['angry', 'stomp', 'grumpy'])
const DROOP_CLIPS: ReadonlySet<string> = new Set(['sad', 'sulk', 'cry'])
/** Standing-still clips that breathe (a looping sheet idle breathes on top of its own drawing). */
const BREATHING: ReadonlySet<string> = new Set(['idle', 'talk', 'carry', 'hold', 'pickup_idle', 'back_idle', 'x:back_idle'])
/** Clips whose own drawing already leaves the floor: no extra bob. */
const AIRBORNE: ReadonlySet<string> = new Set(['jump', 'hop', 'fly', 'swim', 'jump_joyful', 'ride'])

export const STILL: Readonly<Liveliness> = Object.freeze({ sx: 1, sy: 1, lift: 0, lean: 0, shadow: 1 })

export function livelinessFor(a: ActorState, t: number, opts: LivelinessOptions): Liveliness {
  const onFloor = !a.mount
  if (opts.reducedMotion === true || !a.visible) return { ...STILL, shadow: onFloor ? 1 : 0 }
  let sx = 1
  let sy = 1
  let lift = 0
  let lean = 0
  let walking = false

  // ---- walk: bob with the steps, lean into the travel, set off with a squash, settle on arrival
  const st = a.stride
  if (st && st.dur > 0) {
    const tl = t - st.since
    const len = Math.hypot(st.dx * ASPECT, st.dy)
    const sideways = Math.abs(st.dx) * ASPECT >= Math.abs(st.dy)
    const run = st.clip === 'run'
    if (tl >= 0 && tl < st.dur && len > 0.5) {
      walking = true
      const inRamp = st.easeIn ? clamp01(tl / RAMP_S) : 1
      const outRamp = st.easeOut ? clamp01((st.dur - tl) / RAMP_S) : 1
      const speed = smooth(Math.min(inRamp, outRamp))
      if (!AIRBORNE.has(st.clip) && !st.air) {
        // One bob per step; a step is about a quarter of the body's height — the bob follows the
        // distance the feet really covered, so it slows with them at either end.
        const steps = (st.k * len) / Math.max(1, 0.26 * opts.heightPct)
        lift += (run ? 0.022 : 0.011) * Math.abs(Math.sin(Math.PI * steps)) * speed
      }
      if (sideways && !st.air) lean += (run ? 0.085 : 0.05) * speed * Math.sign(st.dx)
      if (st.easeIn && tl < SET_OFF_S && !st.air) {
        const b = bell(tl / SET_OFF_S)
        sy *= 1 - 0.05 * b
        sx *= 1 + 0.03 * b
      }
    } else if (st.easeOut && tl >= st.dur && tl < st.dur + SETTLE_S && len > 0.5) {
      // Arrival: squash onto the planted feet, a small overshoot, then still.
      const x = (tl - st.dur) / SETTLE_S
      const d = 0.05 * Math.exp(-4 * x) * Math.cos(2.6 * Math.PI * x) * (1 - x)
      sy *= 1 - d
      sx *= 1 + d * 0.6
      if (sideways) lean -= 0.03 * Math.sign(st.dx) * Math.exp(-5 * x) * Math.sin(Math.PI * x * 2)
    }
  }

  // ---- speech: a bounce on every word, a slight alternating tilt
  const v = a.voice
  if (a.speaking && v) {
    const tl = t - v.since
    let j = -1
    for (let i = 0; i < v.words.length; i += 1) {
      if ((v.words[i] as { s: number }).s <= tl) j = i
      else break
    }
    if (j >= 0) {
      const w = v.words[j] as { s: number; e: number }
      const len = Math.min(0.26, Math.max(0.12, w.e - w.s + 0.04))
      const b = bell((tl - w.s) / len)
      const amp = walking ? 0.012 : 0.024
      sy *= 1 + amp * b
      sx *= 1 - amp * 0.45 * b
      lean += 0.013 * b * (j % 2 === 0 ? 1 : -1)
    }

    // ---- the feeling it is said with
    const x = tl
    const facing = a.facing
    switch (feelingOf(v.emotion)) {
      case 'happy': {
        if (!walking) {
          const up = bell(x / 0.42)
          lift += 0.045 * up
          sy *= 1 + 0.045 * up
          sx *= 1 - 0.02 * up
          const land = bell((x - 0.42) / 0.2)
          sy *= 1 - 0.05 * land
          sx *= 1 + 0.03 * land
        }
        break
      }
      case 'surprised': {
        const b = bell(x / 0.32)
        sy *= 1 + 0.08 * b
        sx *= 1 - 0.04 * b
        if (!walking) lift += 0.022 * b
        lean -= 0.03 * facing * b
        break
      }
      case 'sad': {
        const k = smooth(x / 0.5)
        sy *= 1 - 0.03 * k
        sx *= 1 + 0.01 * k
        lean += 0.025 * facing * k
        break
      }
      case 'angry': {
        if (x < 0.7) lean += 0.03 * Math.sin(TAU * 7 * x) * (1 - x / 0.7)
        const stomp = bell(x / 0.24)
        sy *= 1 - 0.05 * stomp
        sx *= 1 + 0.035 * stomp
        break
      }
      case 'scared':
        lean += 0.009 * Math.sin(TAU * 11 * x)
        sy *= 1 - 0.015 * smooth(x / 0.3)
        break
      case 'curious':
        lean -= 0.035 * facing * smooth(x / 0.35)
        break
      default:
        break
    }
  }

  // ---- listening: lean in toward the speaker, a small nod every few of their words
  const other = opts.listenTo
  if (!a.speaking && !walking && other?.speaking && other.voice) {
    const tl = t - other.voice.since
    const dir = Math.sign(other.pos[0] - a.pos[0])
    if (dir === a.facing) lean += 0.016 * dir * smooth(tl / 0.5)
    const words = other.voice.words
    for (let i = 2; i < words.length; i += 3) {
      const w = words[i] as { s: number }
      if (w.s > tl) break
      const b = bell((tl - w.s) / 0.34)
      if (b > 0) sy *= 1 - 0.014 * b
    }
  }

  // ---- expressive gestures pop / stomp / droop as they start
  const sinceClip = t - a.clipStartedAt
  if (POP_CLIPS.has(a.clip)) {
    const b = bell(sinceClip / 0.26)
    sy *= 1 + 0.06 * b
    sx *= 1 - 0.03 * b
  } else if (STOMP_CLIPS.has(a.clip)) {
    const b = bell(sinceClip / 0.2)
    sy *= 1 - 0.05 * b
    sx *= 1 + 0.035 * b
  } else if (DROOP_CLIPS.has(a.clip)) {
    sy *= 1 - 0.02 * smooth(sinceClip / 0.5)
  }

  // ---- standing still: breathe
  if (!walking && !a.speaking && BREATHING.has(a.clip)) {
    const b = Math.sin((TAU * (t + opts.phase)) / 2.6)
    sy *= 1 + 0.009 * b
    sx *= 1 - 0.004 * b
  }

  const shadow = onFloor ? 1 - Math.min(0.6, lift * 4) : 0
  return { sx, sy, lift, lean, shadow }
}

/* ------------------------------------------------------------------- dust */

/** A puff of dust (stage %) to draw now: where a real walk sets off and where it stops. */
export interface WalkPuff {
  id: string
  t0: number
  at: [number, number]
  scale: number
}

/**
 * The kicked-up dust of this actor's walk, derived from its stride (not stored in the snapshot's
 * effects): a set-off puff and a stop puff, each alive about a second. A chained leg neither sets
 * off nor stops; short shuffles raise none.
 */
export function walkPuffs(a: Pick<ActorState, 'stride' | 'visible'>, t: number): WalkPuff[] {
  const st = a.stride
  if (!st || st.air || !a.visible || st.dur < 0.6 || Math.hypot(st.dx * ASPECT, st.dy) < 8) return []
  const out: WalkPuff[] = []
  if (st.easeIn && t >= st.since && t < st.since + 1.2) out.push({ id: `${st.id}~go`, t0: st.since, at: [st.from[0], st.from[1]], scale: 0.45 })
  const end = st.since + st.dur
  if (st.easeOut && !st.exit && t >= end && t < end + 1.2) {
    out.push({ id: `${st.id}~stop`, t0: end, at: [st.from[0] + st.dx, st.from[1] + st.dy], scale: 0.35 })
  }
  return out
}

/* ------------------------------------------------------------------ camera */

export interface CameraKick {
  /** Zoom multiplier (≥ 1) about `focus`. */
  zoom: number
  /** Stage-% point the punch-in closes on (it stays put on screen). */
  focus: [number, number] | null
  /** Shake, stage %. */
  dx: number
  dy: number
}

const PUNCH: Readonly<Record<string, number>> = { exclaim: 0.035, stars: 0.045, confetti: 0.03, question: 0.018, splash: 0.03 }
const SHAKE: Readonly<Record<string, number>> = { stars: 0.3, splash: 0.35 }

/** Punch-in envelope: in over 0.1 s, out over ~0.8 s. */
function punchEnv(x: number): number {
  if (x < 0 || x > 1.2) return 0
  return x < 0.1 ? smooth(x / 0.1) : Math.exp(-(x - 0.1) * 4.5)
}

/**
 * A short push-in toward the moment something happens — a `!`, a burst of stars, confetti, a
 * splash, a surprised line — and a small decaying shake on an impact. Story time only; the caller
 * skips it under reduced motion and low distraction. Walk dust (`~` ids) never moves the camera.
 */
export function cameraKick(state: StageState): CameraKick {
  const t = state.t
  let best = 0
  let focus: [number, number] | null = null
  let dx = 0
  let dy = 0
  const pointOf = (at: StageState['fx'][number]['at']): [number, number] | null => {
    if ('at' in at) return [at.at[0], at.at[1]]
    if ('actor' in at) {
      const a = state.actors[at.actor]
      return a ? [a.pos[0], a.pos[1] - 12] : null
    }
    const p = state.props[at.prop]
    if (!p) return null
    const holder = p.heldBy ? state.actors[p.heldBy] : undefined
    return holder ? [holder.pos[0], holder.pos[1] - 12] : [p.pos[0], p.pos[1]]
  }
  for (const f of state.fx) {
    if (f.id.includes('~')) continue
    const x = t - f.t0
    const a = (PUNCH[f.kind] ?? 0) * punchEnv(x)
    if (a > best) {
      best = a
      focus = pointOf(f.at)
    }
    const s = SHAKE[f.kind]
    if (s && x >= 0 && x < 0.45) {
      const decay = Math.exp(-9 * x)
      dx += s * Math.sin(TAU * 13 * x) * decay
      dy += s * 0.6 * Math.sin(TAU * 17 * x + 1) * decay
    }
  }
  for (const a of Object.values(state.actors)) {
    if (!a.speaking || !a.voice || feelingOf(a.voice.emotion) !== 'surprised') continue
    const k = 0.025 * punchEnv(t - a.voice.since)
    if (k > best) {
      best = k
      focus = [a.pos[0], a.pos[1] - 12]
    }
  }
  return { zoom: 1 + best, focus, dx, dy }
}
